/**
 * Text wrapping utilities for canvas text rendering
 * Extracted from AddCanvasTemplate.js to reduce file size
 */

// Helper function to break long words into chunks that fit maxWidth
export function breakLongWord(word, maxWidth, ctx) {
  // Break a single long word into chunks that fit maxWidth
  // Ensure ALL characters are included in the result
  let result = [];
  let subStr = "";

  for (let i = 0; i < word.length; i++) {
    subStr += word[i];
    if (ctx.measureText(subStr).width > maxWidth) {
      if (subStr.length > 1) {
        result.push(subStr.slice(0, -1));
        subStr = word[i];
      } else {
        // Single character is too wide, still push it
        result.push(subStr);
        subStr = "";
      }
    }
  }

  // Always add the remaining substring to ensure no text is lost
  if (subStr) {
    result.push(subStr);
  }

  // Verify that all characters from the original word are included
  const totalLength = result.reduce((sum, chunk) => sum + chunk.length, 0);
  if (totalLength !== word.length) {
    // If we somehow lost characters, add the missing ones as a separate chunk
    if (totalLength < word.length) {
      const missingChars = word.slice(totalLength);
      if (missingChars) {
        result.push(missingChars);
      }
    }
  }

  return result;
}

// Helper function to wrap plain text to multiple lines
export function wrapTextToLines(text, maxWidth, ctx, fontSize = 16) {
  if (!text) return [];

  // Set the font for measurement
  ctx.font = `${fontSize}px Roboto`;

  const lines = [];
  const words = text.split(/\s+/);
  let currentLine = "";

  for (const word of words) {
    if (!word) continue;

    const testLine = currentLine ? currentLine + " " + word : word;
    const testWidth = ctx.measureText(testLine).width;

    if (testWidth > maxWidth && currentLine) {
      // Current line is full, start a new line
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
      }
      currentLine = word;
    } else if (ctx.measureText(word).width > maxWidth) {
      // Single word is too long, break it
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
      }

      // Break the long word into chunks
      let subStr = "";
      for (let i = 0; i < word.length; i++) {
        subStr += word[i];
        if (ctx.measureText(subStr).width > maxWidth) {
          if (subStr.length > 1) {
            lines.push(subStr.slice(0, -1));
            subStr = word[i];
          } else {
            // Single character is too wide, still push it
            lines.push(subStr);
            subStr = "";
          }
        }
      }
      if (subStr) {
        currentLine = subStr;
      } else {
        currentLine = "";
      }
    } else {
      currentLine = testLine;
    }
  }

  // Add the last line if it has content
  if (currentLine.trim()) {
    lines.push(currentLine.trim());
  }

  return lines;
}

// Helper to wrap fragments into lines, preserving all styling
export function wrapFragmentsToLines(
  ctx,
  fragments,
  maxWidth,
  useTolerance = true
) {
  const lines = [];
  let currentLine = [];
  let currentLineWidth = 0;

  fragments.forEach((frag, fragIndex) => {
    // Clean up the text first to remove extra spaces
    const cleanText = frag.text.replace(/\s+/g, " ").trim();
    if (!cleanText) return;

    // Check if this fragment has a different font size than the previous one
    // If so, force a line break to prevent font size bleeding
    if (fragIndex > 0 && currentLine.length > 0) {
      const prevFragment = fragments[fragIndex - 1];
      const currentFontSize = frag.fontSize;
      const prevFontSize = prevFragment.fontSize;

      // If font sizes are different and neither is null, force a line break
      if (
        currentFontSize !== prevFontSize &&
        currentFontSize !== null &&
        prevFontSize !== null
      ) {
        lines.push(currentLine);
        currentLine = [];
        currentLineWidth = 0;
      }
    }

    // Set the font to match the fragment's properties for accurate measurement
    let fontStyle = "";
    if (frag.fontStyle === "italic") fontStyle += "italic ";
    if (frag.fontWeight === "bold") fontStyle += "bold ";
    if (!fontStyle) fontStyle = "normal ";

    // Use fragment's fontSize if available, otherwise extract from ctx.font
    let fragmentFontSize = frag.fontSize;
    if (!fragmentFontSize) {
      const fontSizeMatch = ctx.font.match(/(\d+)px/);
      fragmentFontSize = fontSizeMatch ? parseInt(fontSizeMatch[1]) : 16;
    }

    ctx.font = `${fontStyle}${fragmentFontSize}px Roboto`;

    // Always break down into words for better control over line wrapping
    const words = cleanText.split(" ");
    words.forEach((word, wordIndex) => {
      if (!word) return;

      const wordWidth = ctx.measureText(word).width;
      const spaceWidth = ctx.measureText(" ").width;

      // Add space before word if it's not the first word in the first fragment
      const needsSpace = !(fragIndex === 0 && wordIndex === 0);
      const totalWordWidth = needsSpace ? wordWidth + spaceWidth : wordWidth;

      // Check if the word itself is too long for the line
      if (wordWidth > maxWidth) {
        // If there's content in the current line, push it first
        if (currentLine.length > 0) {
          lines.push(currentLine);
          currentLine = [];
          currentLineWidth = 0;
        }

        // Break the long word into chunks - ensure ALL text is rendered
        const broken = breakLongWord(word, maxWidth, ctx);
        broken.forEach((chunk) => {
          if (chunk && chunk.trim()) {
            // Only add non-empty chunks
            lines.push([
              {
                text: chunk,
                color: frag.color,
                backgroundColor: frag.backgroundColor,
                textDecoration: frag.textDecoration,
                fontWeight: frag.fontWeight,
                fontStyle: frag.fontStyle,
                fontSize: frag.fontSize,
              },
            ]);
          }
        });
      } else if (
        currentLineWidth + totalWordWidth > maxWidth &&
        currentLine.length > 0
      ) {
        // Current line is full, start a new line
        // Ensure we don't lose any text by being more flexible with wrapping

        lines.push(currentLine);
        currentLine = [
          {
            text: word,
            color: frag.color,
            backgroundColor: frag.backgroundColor,
            textDecoration: frag.textDecoration,
            fontWeight: frag.fontWeight,
            fontStyle: frag.fontStyle,
            fontSize: frag.fontSize,
          },
        ];
        currentLineWidth = wordWidth;
      } else {
        // Add space before word if needed
        if (needsSpace && currentLine.length > 0) {
          currentLine.push({
            text: " ",
            color: frag.color,
            backgroundColor: null, // Spaces don't need background
            textDecoration: "none", // Spaces don't need decoration
            fontWeight: "normal", // Spaces use normal weight
            fontStyle: "normal", // Spaces use normal style
            fontSize: null, // Spaces always use default font size
          });
          currentLineWidth += spaceWidth;
        }

        // Add word to current line
        currentLine.push({
          text: word,
          color: frag.color,
          backgroundColor: frag.backgroundColor,
          textDecoration: frag.textDecoration,
          fontWeight: frag.fontWeight,
          fontStyle: frag.fontStyle,
          fontSize: frag.fontSize,
        });
        currentLineWidth += wordWidth;
      }
    });
  });

  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  // Debug: Show each line with its font sizes
  lines.forEach((line, lineIndex) => {
    // Debug logging can be added here if needed
  });

  return lines;
}
